import React, { useEffect, useState } from "react";
import DashboardLayout from "@/components/layout/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import { formatINR } from "@/lib/constants";
import { toast } from "sonner";
import { Wallet as WalletIcon, Plus, ArrowDownCircle, ArrowUpCircle, AlertCircle, RefreshCcw, Clock } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { trackEvent } from "@/lib/analytics";

// Lazy-load a <script> once
const loadedScripts = new Set();
function loadScript(src) {
  return new Promise((resolve, reject) => {
    if (loadedScripts.has(src)) return resolve();
    const s = document.createElement("script");
    s.src = src;
    s.async = true;
    s.onload = () => { loadedScripts.add(src); resolve(); };
    s.onerror = () => reject(new Error(`Failed to load ${src}`));
    document.body.appendChild(s);
  });
}

export default function Wallet() {
  const { user, refreshUser } = useAuth();
  const [balance, setBalance] = useState(user?.wallet_balance || 0);
  const [ledger, setLedger] = useState([]);
  const [topupOpen, setTopupOpen] = useState(false);
  const [amount, setAmount] = useState("");
  const [method, setMethod] = useState("upi");
  const [gateway, setGateway] = useState("");

  // Two-fee preview (MDR + Settlement). Recomputed when amount/gateway change.
  const [feePreview, setFeePreview] = useState(null);
  const [feeError, setFeeError] = useState("");
  const [feeLoading, setFeeLoading] = useState(false);
  const [pg, setPg] = useState("");
  const [gwConfig, setGwConfig] = useState(null);
  const [pgFeasibility, setPgFeasibility] = useState(null);
  const [loading, setLoading] = useState(false);
  const [listLoading, setListLoading] = useState(true);
  const [pendingTopups, setPendingTopups] = useState([]);
  const [checkingStatus, setCheckingStatus] = useState(null);
  // Saved-card picker (applies to ALL gateways when method=card — Feb 2026 policy).
  // We NEVER take a raw card number from the user; instead they pick an
  // already-approved payment mode. ManaRecharge additionally receives the
  // last-4 (Postman spec); other PGs get the mode_id recorded for audit only.
  // NOTE: `cardPickerNeeded` MUST be declared here — BEFORE the useEffects
  // below reference it, or React hits a temporal-dead-zone ReferenceError
  // that white-screens the whole Wallet page on first render.
  const [savedCards, setSavedCards] = useState([]);
  const [savedCardsLoading, setSavedCardsLoading] = useState(false);
  const [selectedPaymentModeId, setSelectedPaymentModeId] = useState("");
  const cardPickerNeeded = method === "card";

  useEffect(() => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0 || !gateway) {
      setFeePreview(null);
      setFeeError("");
      return;
    }
    let cancel = false;
    setFeeLoading(true);
    setFeeError("");
    const t = setTimeout(async () => {
      try {
        const { data } = await api.post("/topup-fees/preview", { gateway, amount: amt, channel: pg || null });
        if (!cancel) setFeePreview(data);
      } catch (err) {
        if (!cancel) {
          setFeePreview(null);
          setFeeError(formatApiError(err.response?.data?.detail) || "Fee preview unavailable");
        }
      } finally {
        if (!cancel) setFeeLoading(false);
      }
    }, 350);
    return () => { cancel = true; clearTimeout(t); };
  }, [amount, gateway, pg]);

  // Load user's approved cards whenever the ManaRecharge + card combination
  // becomes active. Runs once per (gateway, method, topupOpen) change.
  useEffect(() => {
    if (!cardPickerNeeded || !topupOpen) return;
    let cancel = false;
    setSavedCardsLoading(true);
    (async () => {
      try {
        const { data } = await api.get("/wallet/payment-modes", {
          params: { mode_type: "card", status: "approved" },
        });
        if (!cancel) {
          const cards = data?.modes || [];
          setSavedCards(cards);
          // Auto-select the newest card if the user has exactly one.
          if (cards.length === 1) setSelectedPaymentModeId(cards[0].id);
        }
      } catch {
        if (!cancel) setSavedCards([]);
      } finally {
        if (!cancel) setSavedCardsLoading(false);
      }
    })();
    return () => { cancel = true; };
  }, [cardPickerNeeded, topupOpen]);

  const load = async () => {
    setListLoading(true);
    try {
      const [b, l, g, t, f] = await Promise.all([
        api.get("/wallet/balance"),
        api.get("/wallet/ledger?limit=100"),
        api.get("/wallet/gateways/config"),
        api.get("/wallet/topups?limit=20"),
        api.get("/wallet/available-pgs").catch(() => ({ data: null })),
      ]);
      setBalance(b.data.balance);
      setLedger(l.data || []);
      setGwConfig(g.data);
      setPendingTopups((t.data || []).filter((tp) => tp.status !== "success"));
      if (f.data) {
        setPgFeasibility(f.data);
        const gateways = Array.isArray(f.data.gateways) ? f.data.gateways : [];
        const first = gateways.find((gw) => gw.allowed);
        if (first && !gateway) {
          setGateway(first.key);
          // Pick the first channel that is usable. Prefer allowed AND active,
          // but fall back to just allowed (package fallback path where PG
          // status is momentarily unreachable — see admin_payment_links.py).
          const channelList = Array.isArray(first.channels) ? first.channels : [];
          const preferred = channelList.find((c) => c && c.id && c.allowed && c.active !== false);
          const anyAllowed = channelList.find((c) => c && c.id && c.allowed);
          const firstCh = first.supports_channel
            ? String((preferred || anyAllowed)?.id || "")
            : "";
          setPg(firstCh);
        }
      } else {
        if (g.data?.razorpay?.enabled) setGateway("razorpay");
        else if (g.data?.cashfree?.enabled) setGateway("cashfree");
        else if (g.data?.manarecharge?.enabled) setGateway("manarecharge");
        else if (g.data?.runpaisa?.enabled) setGateway("runpaisa");
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Failed to load wallet");
    } finally {
      setListLoading(false);
    }
  };

  const checkTopupStatus = async (topupId) => {
    setCheckingStatus(topupId);
    try {
      const { data } = await api.get(`/wallet/topup/${topupId}/status`);
      toast.success(data.message || "Status checked");
      if (data.topup?.status === "success") {
        await load();
      } else {
        setPendingTopups((prev) => prev.map((tp) => tp.id === topupId ? (data.topup || tp) : tp));
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Status check failed");
    } finally {
      setCheckingStatus(null);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const refreshBalance = async () => {
    const { data } = await api.get("/wallet/balance");
    setBalance(data.balance);
    refreshUser();
  };

  const payWithRazorpay = async ({ topup, sdk_params }) => {
    await loadScript(gwConfig.razorpay.sdk_url);
    return new Promise((resolve, reject) => {
      const rzp = new window.Razorpay({
        ...sdk_params,
        method: method === "card" ? { card: true } : method === "upi" ? { upi: true } : { netbanking: true },
        handler: async (res) => {
          try {
            const r = await api.post("/wallet/topup/razorpay/verify", {
              reference: topup.id,
              razorpay_order_id: res.razorpay_order_id,
              razorpay_payment_id: res.razorpay_payment_id,
              razorpay_signature: res.razorpay_signature,
            });
            resolve(r.data);
          } catch (err) {
            reject(err);
          }
        },
        modal: {
          ondismiss: () => reject(new Error("Payment window closed")),
        },
      });
      rzp.on("payment.failed", (resp) => {
        reject(new Error(resp.error?.description || "Payment failed"));
      });
      rzp.open();
    });
  };

  const payWithCashfree = async ({ topup, sdk_params }) => {
    await loadScript(gwConfig.cashfree.sdk_url);
    const cashfree = window.Cashfree({ mode: sdk_params.mode });
    const result = await cashfree.checkout({
      paymentSessionId: sdk_params.payment_session_id,
      redirectTarget: "_modal",
    });
    if (result?.error) throw new Error(result.error.message || "Payment error");
    if (result?.paymentDetails?.paymentMessage && (result?.paymentDetails?.paymentStatus || "").toUpperCase() !== "SUCCESS") {
      throw new Error(result.paymentDetails.paymentMessage);
    }
    // Ask our backend to pull the final status
    const { data } = await api.post("/wallet/topup/cashfree/verify", { reference: topup.id });
    return data;
  };

  const payWithManaRecharge = async ({ topup, sdk_params }) => {
    return pollHostedCheckout({ topup, sdk_params, gateway: "manarecharge" });
  };

  const payWithRunpaisa = async ({ topup, sdk_params }) => {
    return pollHostedCheckout({ topup, sdk_params, gateway: "runpaisa" });
  };

  // Shared hosted-checkout poller for ManaRecharge / Runpaisa.
  const pollHostedCheckout = async ({ topup, sdk_params, gateway }) => {
    const w = window.open(sdk_params.checkout_url, `${gateway}_pg`, "width=480,height=720");
    if (!w) throw new Error("Pop-up blocked. Allow pop-ups and retry.");
    const start = Date.now();
    const timeoutMs = 5 * 60 * 1000;
    while (Date.now() - start < timeoutMs) {
      await new Promise((r) => setTimeout(r, 4000));
      const closed = w.closed;
      try {
        const { data } = await api.post(`/wallet/topup/${gateway}/verify`, { reference: topup.id });
        return data;
      } catch (err) {
        const code = err.response?.status;
        if (code === 402) {
          if (closed) throw new Error("Checkout closed before payment was completed");
          continue;
        }
        throw err;
      }
    }
    throw new Error("Payment timed out — please refresh to check status");
  };

  const doTopup = async (e) => {
    e.preventDefault();
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) {
      toast.error("Enter a valid amount");
      return;
    }
    if (!gwConfig?.[gateway]?.enabled) {
      toast.error(`${gateway} is not configured. Ask admin to set the API keys.`);
      return;
    }
    setLoading(true);
    try {
      // For ManaRecharge card top-ups, the backend now requires a
      // pre-approved payment_mode_id (Feb 2026 API) — we never send raw card
      // numbers. Guard here so the API call doesn't waste a round-trip.
      if (cardPickerNeeded && !selectedPaymentModeId) {
        toast.error("Please select an approved card first (or add one under Payment Modes).");
        setLoading(false);
        return;
      }
      const { data: order } = await api.post("/wallet/topup/order", {
        amount: amt, gateway, method, pg: pg || undefined,
        payment_mode_id: cardPickerNeeded ? selectedPaymentModeId : undefined,
      });
      const result = gateway === "razorpay"
        ? await payWithRazorpay(order)
        : gateway === "cashfree"
          ? await payWithCashfree(order)
          : gateway === "manarecharge"
            ? await payWithManaRecharge(order)
            : await payWithRunpaisa(order);
      toast.success(`Top-up success · ${formatINR(amt)} added`);
      trackEvent("wallet_topup", { amount: amt, gateway });
      setBalance(result.balance);
      setAmount("");
      setTopupOpen(false);
      await load();
      refreshUser();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message || "Top-up failed");
      await refreshBalance();
    } finally {
      setLoading(false);
    }
  };

  const bothDisabled = gwConfig && !gwConfig.razorpay.enabled && !gwConfig.cashfree.enabled && !gwConfig.manarecharge?.enabled && !gwConfig.runpaisa?.enabled;

  return (
    <DashboardLayout>
      <div className="animate-fade-up">
        <span className="overline">Wallet</span>
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight mt-2">
          Your balance
        </h1>
        <p className="text-muted-foreground mt-2 max-w-xl">
          Top up your wallet to run BBPS payments and disburse payouts. Every transaction is
          automatically debited from this balance.
        </p>

        <div className="mt-8 grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-1 surface-card p-6 relative overflow-hidden interactive-card">
            <div className="absolute -right-10 -top-10 h-40 w-40 rounded-full bg-primary/10 blur-2xl" />
            <span className="overline">Available balance</span>
            <div className="font-mono text-5xl mt-3 font-semibold tabular-nums" data-testid="wallet-balance">
              {formatINR(balance)}
            </div>
            <div className="text-xs text-muted-foreground mt-2 inline-flex items-center gap-2">
              <WalletIcon className="h-3.5 w-3.5" strokeWidth={1.5} />
              Live · refreshed just now
            </div>
            <Dialog open={topupOpen} onOpenChange={setTopupOpen}>
              <DialogTrigger asChild>
                <Button className="mt-6 w-full" data-testid="open-topup-dialog">
                  <Plus className="h-4 w-4 mr-1" strokeWidth={1.5} /> Top-up wallet
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>Top up your wallet</DialogTitle>
                  <DialogDescription>
                    {gwConfig?.env === "sandbox"
                      ? "Test mode — use Razorpay / Cashfree sandbox cards & UPI IDs."
                      : "Secure payment · live mode"}
                  </DialogDescription>
                </DialogHeader>
                {bothDisabled && (
                  <div className="rounded-md border border-amber-300 bg-amber-50 p-3 text-xs text-amber-900 flex gap-2 items-start" data-testid="gateway-disabled-banner">
                    <AlertCircle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    <div>
                      None of Razorpay, Cashfree, ManaRecharge, or Runpaisa is configured yet. Please ask your admin to add
                      API keys in <code>/admin/integrations</code>.
                    </div>
                  </div>
                )}
                <form onSubmit={doTopup} className="space-y-4" data-testid="topup-form">
                  <div>
                    <Label>Gateway</Label>
                    <div className="grid grid-cols-2 gap-2 mt-1">
                      {(Array.isArray(pgFeasibility?.gateways) ? pgFeasibility.gateways : []).map((g) => (
                        <button
                          type="button"
                          key={g.key}
                          disabled={!g.allowed}
                          onClick={() => {
                            setGateway(g.key);
                            // Match load()'s picker: prefer allowed && active,
                            // fall back to just allowed. Keeps the channel
                            // selection consistent when the partner switches
                            // gateways manually on a package-fallback payload.
                            const chList = Array.isArray(g.channels) ? g.channels : [];
                            const preferred = chList.find((c) => c && c.id && c.allowed && c.active !== false);
                            const anyAllowed = chList.find((c) => c && c.id && c.allowed);
                            const firstCh = g.supports_channel
                              ? String((preferred || anyAllowed)?.id || "")
                              : "";
                            setPg(firstCh);
                          }}
                          data-testid={`gateway-${g.key}`}
                          className={`rounded-md border px-3 py-2 text-sm font-medium transition-colors ${
                            gateway === g.key && g.allowed
                              ? "border-primary bg-primary/10 text-primary"
                              : g.allowed
                                ? "border-border hover:bg-accent"
                                : "border-border bg-muted/40 text-muted-foreground cursor-not-allowed"
                          }`}
                        >
                          {g.label}
                          {!g.allowed && <div className="text-[10px] opacity-70">not in package</div>}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* PG Channel dropdown */}
                  {(() => {
                    const gwEntry = (Array.isArray(pgFeasibility?.gateways) ? pgFeasibility.gateways : []).find((g) => g.key === gateway);
                    if (!gwEntry?.supports_channel) return null;
                    const channels = Array.isArray(gwEntry.channels) ? gwEntry.channels : [];
                    // Show every channel with a truthy id. The `active` and
                    // `allowed` flags become visual (disabled) hints instead
                    // of hiding rows outright — otherwise a temporarily
                    // inactive PG channel makes the whole picker disappear
                    // (the "channel option not showing" bug).
                    const visibleChannels = channels.filter((c) => c && c.id);
                    return (
                      <div>
                        <Label>PG Channel</Label>
                        {visibleChannels.length === 0 ? (
                          <div className="mt-1 text-xs rounded-md border border-amber-500/40 bg-amber-500/5 p-2 text-amber-800 dark:text-amber-400">
                            No channels available yet. Ask admin to add channels to your package for ManaRecharge.
                          </div>
                        ) : (
                          <Select value={pg || undefined} onValueChange={setPg}>
                            <SelectTrigger className="mt-1 font-mono" data-testid="topup-pg-channel"><SelectValue placeholder="Select channel" /></SelectTrigger>
                            <SelectContent>
                              {visibleChannels.map((c) => {
                                const disabled = !c.allowed || c.active === false;
                                const suffix = !c.allowed
                                  ? " (locked)"
                                  : (c.active === false ? " (offline)" : "");
                                return (
                                  <SelectItem key={String(c.id)} value={String(c.id)} disabled={disabled}>
                                    {c.label || String(c.id)}{suffix}
                                  </SelectItem>
                                );
                              })}
                            </SelectContent>
                          </Select>
                        )}
                      </div>
                    );
                  })()}

                  <div>
                    <Label>Amount (₹)</Label>
                    <Input
                      type="number"
                      min="1"
                      step="any"
                      value={amount}
                      onChange={(e) => setAmount(e.target.value)}
                      className="mt-1 font-mono text-lg"
                      data-testid="topup-amount"
                      placeholder="10000"
                    />
                  </div>
                  <div>
                    <Label>Preferred method</Label>
                    <Select value={method} onValueChange={setMethod}>
                      <SelectTrigger className="mt-1" data-testid="topup-method">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="upi">UPI</SelectItem>
                        <SelectItem value="card">Debit / Credit Card</SelectItem>
                        <SelectItem value="netbanking">Net banking</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Saved card picker — required for card top-ups on ALL PGs.
                      Raw card PAN is NEVER captured here; users pick from
                      admin-approved payment_mode_whitelist rows. ManaRecharge
                      additionally forwards the last-4 to its initiate_payment
                      endpoint; other PGs use it for audit only. */}
                  {cardPickerNeeded && (
                    <div data-testid="saved-card-picker">
                      <Label>Choose saved card</Label>
                      {savedCardsLoading ? (
                        <div className="mt-1 text-xs text-muted-foreground border border-border rounded-md p-3">
                          Loading approved cards…
                        </div>
                      ) : savedCards.length === 0 ? (
                        <div className="mt-1 rounded-md border border-amber-500/40 bg-amber-500/5 p-3 text-xs">
                          <div className="font-medium text-amber-800 dark:text-amber-400">
                            No approved cards on file
                          </div>
                          <p className="text-muted-foreground mt-1">
                            Add a card under <span className="font-medium">Payment Modes</span> — an admin has to
                            approve it before it becomes selectable here. Card top-ups now require a pre-verified
                            card on every gateway.
                          </p>
                        </div>
                      ) : (
                        <>
                          <Select
                            value={selectedPaymentModeId}
                            onValueChange={setSelectedPaymentModeId}
                          >
                            <SelectTrigger className="mt-1" data-testid="saved-card-select">
                              <SelectValue placeholder="Select a card…" />
                            </SelectTrigger>
                            <SelectContent>
                              {savedCards.map((c) => (
                                <SelectItem
                                  key={c.id}
                                  value={c.id}
                                  data-testid={`saved-card-${c.id}`}
                                >
                                  •••• •••• •••• {c.mode_detail || "----"}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          <p className="mt-1.5 text-[10px] text-muted-foreground">
                            Only the last 4 digits are shared — your full card number stays on the PG checkout page.
                          </p>
                        </>
                      )}
                    </div>
                  )}
                  <div className="flex gap-2 flex-wrap">
                    {[1000, 5000, 10000, 25000, 50000].map((v) => (
                      <button
                        key={v}
                        type="button"
                        data-testid={`preset-${v}`}
                        onClick={() => setAmount(String(v))}
                        className="text-xs px-3 py-1 rounded-full border border-border hover:bg-accent transition-colors font-mono"
                      >
                        +{formatINR(v)}
                      </button>
                    ))}
                  </div>

                  {(feePreview || feeError || feeLoading) && (
                    <div
                      className={`rounded-md border p-3 text-xs ${feeError ? "border-destructive/40 bg-destructive/5" : "border-border bg-muted/30"}`}
                      data-testid="topup-fee-preview"
                    >
                      {feeLoading ? (
                        <div className="text-muted-foreground">Calculating PG fees…</div>
                      ) : feeError ? (
                        <div className="text-destructive font-medium">{feeError}</div>
                      ) : feePreview ? (
                        <div className="space-y-1.5">
                          <div className="flex justify-between"><span className="text-muted-foreground">You'll pay at PG</span><span className="font-mono font-semibold" data-testid="fee-gross">{formatINR(feePreview.gross_amount)}</span></div>
                          <div className="flex justify-between text-muted-foreground"><span>· PG MDR fee</span><span className="font-mono">+ {formatINR(feePreview.mdr_fee)}</span></div>
                          <div className="flex justify-between text-muted-foreground"><span>· Settlement fee deducted</span><span className="font-mono">− {formatINR(feePreview.settle_fee)}</span></div>
                          <div className="flex justify-between pt-1 border-t border-border"><span className="font-medium">Wallet credit</span><span className="font-mono font-semibold text-success" data-testid="fee-credited">{formatINR(feePreview.credited_amount)}</span></div>
                          <div className="text-[10px] text-muted-foreground pt-1">Fee source: <span className="font-mono">{feePreview.fee_source}</span></div>
                        </div>
                      ) : null}
                    </div>
                  )}

                  <DialogFooter>
                    <Button
                      type="submit"
                      disabled={
                        loading ||
                        bothDisabled ||
                        !!feeError ||
                        (cardPickerNeeded && !selectedPaymentModeId)
                      }
                      data-testid="submit-topup"
                    >
                      {loading ? "Processing…" : `Pay via ${gateway === "razorpay" ? "Razorpay" : gateway === "cashfree" ? "Cashfree" : gateway === "manarecharge" ? "ManaRecharge" : "Runpaisa"}`}
                    </Button>
                  </DialogFooter>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          <div className="lg:col-span-2 surface-card overflow-hidden">
            <div className="p-6 pb-4 flex items-center justify-between">
              <div>
                <span className="overline">Ledger</span>
                <h3 className="font-display text-xl font-bold tracking-tight mt-1">
                  Recent activity
                </h3>
              </div>
              <Badge variant="outline" className="font-mono text-[10px]">
                last 100
              </Badge>
            </div>
            <Table data-testid="wallet-ledger">
              <TableHeader>
                <TableRow>
                  <TableHead>When</TableHead>
                  <TableHead>Reason</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead className="text-right">Balance after</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {listLoading ? (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center py-10 text-muted-foreground">Loading…</TableCell>
                  </TableRow>
                ) : ledger.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center py-12 text-muted-foreground">
                      No activity yet. Top up to get started.
                    </TableCell>
                  </TableRow>
                ) : (
                  ledger.map((l) => (
                    <TableRow key={l.id} data-testid={`ledger-row-${l.id}`}>
                      <TableCell className="text-xs text-muted-foreground font-mono">
                        {new Date(l.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {l.type === "credit" ? (
                            <ArrowDownCircle className="h-4 w-4 text-success" strokeWidth={1.5} />
                          ) : (
                            <ArrowUpCircle className="h-4 w-4 text-destructive" strokeWidth={1.5} />
                          )}
                          <div>
                            <div className="text-sm font-medium">{l.reason}</div>
                            <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{l.ref_type}</div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className={`text-right font-mono tabular-nums ${l.type === "credit" ? "text-success" : "text-destructive"}`}>
                        {l.type === "credit" ? "+" : "−"}{formatINR(l.amount)}
                      </TableCell>
                      <TableCell className="text-right font-mono tabular-nums">{formatINR(l.balance_after)}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </div>

        {pendingTopups.length > 0 && (
          <div className="mt-8 surface-card overflow-hidden" data-testid="pending-topups-card">
            <div className="p-6 pb-4 flex items-center justify-between">
              <div>
                <span className="overline">Pending top-ups</span>
                <h3 className="font-display text-xl font-bold tracking-tight mt-1 flex items-center gap-2">
                  <Clock className="h-5 w-5 text-amber-600" strokeWidth={1.5} />
                  Awaiting confirmation
                </h3>
                <p className="text-xs text-muted-foreground mt-1">
                  Top-ups that haven&apos;t been confirmed yet. Click &quot;Check status&quot; to pull the latest from the gateway —
                  if the payment succeeded, your wallet will be credited automatically.
                </p>
              </div>
              <Badge variant="outline" className="font-mono text-[10px]" data-testid="pending-topups-count">
                {pendingTopups.length} pending
              </Badge>
            </div>
            <Table data-testid="pending-topups-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Reference</TableHead>
                  <TableHead>Gateway</TableHead>
                  <TableHead>Amount</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Started</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pendingTopups.map((tp) => (
                  <TableRow key={tp.id} data-testid={`pending-topup-row-${tp.id}`}>
                    <TableCell className="font-mono text-xs text-muted-foreground">
                      {String(tp.id).slice(0, 12)}…
                    </TableCell>
                    <TableCell className="text-sm capitalize">{tp.gateway || tp.method || "—"}</TableCell>
                    <TableCell className="font-mono tabular-nums">{formatINR(tp.amount)}</TableCell>
                    <TableCell>
                      <Badge
                        variant={tp.status === "failed" ? "destructive" : "outline"}
                        className="font-mono text-[10px] uppercase"
                        data-testid={`pending-topup-status-${tp.id}`}
                      >
                        {tp.status || "pending"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground font-mono">
                      {tp.created_at
                        ? new Date(tp.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })
                        : "—"}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => checkTopupStatus(tp.id)}
                        disabled={checkingStatus === tp.id}
                        data-testid={`check-status-btn-${tp.id}`}
                      >
                        <RefreshCcw className={`h-3.5 w-3.5 mr-1 ${checkingStatus === tp.id ? "animate-spin" : ""}`} strokeWidth={1.5} />
                        {checkingStatus === tp.id ? "Checking…" : "Check status"}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
